import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/config';

export const CartDrawer = () => {
  const { cart, loading, updateQuantity, removeFromCart, getCartTotal, setIsOpen, sessionId } = useCart();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout`, {
        origin_url: originUrl,
        session_id: sessionId
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4">
        <ShoppingBag className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="font-heading font-semibold text-lg text-pockett-gray mb-2">
          Your cart is empty
        </h3>
        <p className="text-muted-foreground mb-6">
          Browse our handmade treasures and find something special!
        </p>
        <Button 
          asChild
          className="bg-pockett-purple hover:bg-pockett-purple/90 rounded-full"
          onClick={() => setIsOpen(false)}
          data-testid="continue-shopping-btn"
        >
          <Link to="/shop">Continue Shopping</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Cart Items */}
      <div className="flex-1 overflow-y-auto py-4">
        <div className="space-y-4">
          {cart.items.map((item, index) => (
            <div 
              key={`${item.product_id}-${item.variant_id || 'default'}-${index}`}
              className="flex gap-4 p-3 bg-muted/50 rounded-xl"
              data-testid={`cart-item-${item.product_id}`}
            >
              <img 
                src={item.image_url} 
                alt={item.product_name}
                className="w-20 h-20 object-cover rounded-lg"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-pockett-gray truncate">
                  {item.product_name}
                </h4>
                {item.variant_name && (
                  <p className="text-sm text-muted-foreground">{item.variant_name}</p>
                )}
                <p className="font-semibold text-pockett-purple mt-1">
                  ${item.price.toFixed(2)}
                </p>
                
                {/* Quantity Controls */}
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => updateQuantity(item.product_id, item.variant_id, item.quantity - 1)}
                    disabled={loading || item.quantity <= 1}
                    data-testid={`decrease-qty-${item.product_id}`}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="w-8 text-center font-medium">{item.quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => updateQuantity(item.product_id, item.variant_id, item.quantity + 1)}
                    disabled={loading}
                    data-testid={`increase-qty-${item.product_id}`}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 ml-auto text-destructive hover:text-destructive"
                    onClick={() => removeFromCart(item.product_id, item.variant_id)}
                    disabled={loading}
                    data-testid={`remove-item-${item.product_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cart Footer */}
      <div className="border-t border-border pt-4 space-y-4">
        <div className="flex justify-between items-center">
          <span className="font-medium text-pockett-gray">Subtotal</span>
          <span className="font-heading font-bold text-xl text-pockett-purple">
            ${getCartTotal().toFixed(2)} AUD
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          Shipping calculated at checkout
        </p>
        <Button 
          className="w-full bg-pockett-purple hover:bg-pockett-purple/90 rounded-full h-12 font-semibold"
          onClick={handleCheckout}
          disabled={checkoutLoading || loading}
          data-testid="checkout-btn"
        >
          {checkoutLoading ? 'Processing...' : 'Proceed to Checkout'}
        </Button>
        <Button 
          variant="outline" 
          className="w-full rounded-full"
          asChild
          onClick={() => setIsOpen(false)}
          data-testid="view-cart-btn"
        >
          <Link to="/cart">View Full Cart</Link>
        </Button>
      </div>
    </div>
  );
};

export default CartDrawer;
